import { Component, h, State, Host } from '@stencil/core';
import { fetchData } from './spw-iodda.service';

@Component({
  tag: 'spw-iodda',
  styleUrl: 'spw-iodda.scss',
  shadow: true,
})
export class SpwIodda {
  @State() items: any[] = [];
  @State() currentPage: number = 1;
  @State() totalItems: number = 0;
  @State() isLoading: boolean = false;
  @State() searchTerm: string = '';

  @State() filters = {};

  private itemsPerPage: number = 3;
  private debounceTimer: any;

  componentWillLoad() {
    this.filters = {
      is_featured: false,
      has_launch: false,
    };
    this.initFiltersFromURL();
    this.loadData(this.currentPage);
  }

  private debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }

  private initFiltersFromURL() {
    const queryParams = new URLSearchParams(window.location.search);
    const newFilters = { ...this.filters };
    this.searchTerm = queryParams.get('search') || '';
    Object.keys(newFilters).forEach(filterKey => {
      if (queryParams.has(filterKey)) {
        newFilters[filterKey] = queryParams.get(filterKey) === 'true';
      }
    });
    this.filters = newFilters;
  }

  private updateURLWithFilters() {
    const queryParams = new URLSearchParams();
    if (this.searchTerm) {
      queryParams.set('search', this.searchTerm);
    }
    Object.keys(this.filters).forEach(filterKey => {
      if (this.filters[filterKey]) {
        queryParams.set(filterKey, 'true');
      }
    });
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }

  private async loadData(page: number) {
    this.items = [];
    this.isLoading = true;
    const data = await fetchData(page, this.itemsPerPage, this.searchTerm, this.filters);
    setTimeout(() => {
      try {
        this.items = data.results;
        this.totalItems = data.count;
      } catch (error) {
        console.error('Could not load data:', error);
      } finally {
        this.isLoading = false;
      }
    }, 0);
  }

  private handleFilterChange(filterKey, value) {
    this.filters = { ...this.filters, [filterKey]: value };
    this.updateURLWithFilters();
    this.loadData(this.currentPage);
  }

  private handleSearchInput(event) {
    this.searchTerm = event.detail;
    this.debounce(() => {
      this.currentPage = 1;
      this.updateURLWithFilters();
      this.loadData(1);
    }, 500)();
  }

  private handlePageChanged(event: CustomEvent<number>) {
    this.currentPage = event.detail;
    this.loadData(this.currentPage);
  }

  render() {
    const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    const resultsText = this.totalItems > 1 ? 'résultats trouvés' : 'résultat trouvé';

    return (
      <Host>
        <div class="spw-search-input">
          <spw-text-field
            is-search="true"
            size="large"
            type="text"
            value={this.searchTerm}
            placeholder="Rechercher une démarche"
            onValueChanged={event => this.handleSearchInput(event)}
          ></spw-text-field>
        </div>
        <div hidden class="spw-filter spw-tw-mb-6">
          {Object.keys(this.filters).map(filterKey => (
            <label class="spw-tw-mr-4">
              <input
                type="checkbox"
                checked={this.filters[filterKey]}
                onChange={(event: Event) => this.handleFilterChange(filterKey, (event.target as HTMLInputElement).checked)}
              />
              <span class="spw-tw-ml-2">{filterKey}</span>
            </label>
          ))}
        </div>
        {this.totalItems === 0 && !this.isLoading ? (
          <div class="spw-no-results">
            <h3 class="spw-no-results__title">Aucun résultat ne correspond à votre recherche.</h3>
            <ul class="spw-no-results__list">
              <li>Vérifiez si votre orthographe est correcte</li>
              <li>Supprimez des filtres</li>
              <li>Essayez d’autres termes de recherche</li>
            </ul>
          </div>
        ) : null}
        {this.totalItems > 0 && !this.isLoading ? (
          <p class="spw-results">
            {this.totalItems} {resultsText}
          </p>
        ) : null}
        {this.isLoading ? <spw-loading class="spw-tw-mb-10"></spw-loading> : null}
        <ul class="spw-list">
          {this.items.map(item => (
            <li class="spw-list-item" key={item.name}>
              <a href={item.url} target="_blank">
                <h3 class="spw-list-item__title">{item.title}</h3>
                <p class="spw-list-item__text">{item.summary}</p>
              </a>
            </li>
          ))}
        </ul>
        {totalPages > 1 && (
          <spw-pagination
            class="spw-tw-mt-12"
            currentPage={this.currentPage}
            is-disabled={this.isLoading}
            totalItems={this.totalItems}
            itemsPerPage={this.itemsPerPage}
            onPageChanged={this.handlePageChanged.bind(this)}
          />
        )}
      </Host>
    );
  }
}
